import { isToolUIPart } from 'ai';
import type { IntlShape } from 'react-intl';
import type { ChatUIMessage } from '../hooks/UseAgentChat/schemas';
import { isHiddenTool } from '../utils/message';
import {
  getSubagentRuns,
  getSubagentSteps,
  isAgentToolName,
} from './subagentRuns';
import type { ToolCallDisplay } from './toolCallUtils';
import { toToolCall } from './toolCallUtils';

// Mastra's skill tools name the skill in their input (`skill` as `name`,
// `skill_read` as `skillName`); show it so the status says which skill.
const SKILL_NAME_ARG = new Map([
  ['skill', 'name'],
  ['skill_read', 'skillName'],
]);

type ToolActivityCall = Pick<ToolCallDisplay, 'toolName' | 'args'>;

const getSkillName = ({ toolName, args }: ToolActivityCall) => {
  const argName = SKILL_NAME_ARG.get(toolName);
  const skillName = argName ? args?.[argName] : undefined;
  return typeof skillName === 'string' && skillName ? skillName : undefined;
};

/** The latest unfinished visible tool call of the subagent run for a call. */
const getSubagentCall = (
  parts: ChatUIMessage['parts'],
  agentCallId: string
): ToolActivityCall | undefined => {
  const run = getSubagentRuns(parts).get(agentCallId);
  if (!run) return undefined;

  const steps = getSubagentSteps(parts, run);
  // Done because it is a result or error, not because it has one: a tool
  // returning nothing streams no `result` at all.
  const doneIds = new Set(
    steps.flatMap(step =>
      [...step.toolResults, ...step.toolErrors].map(call => call.toolCallId)
    )
  );

  return [
    ...steps.flatMap(step => step.toolCalls),
    ...run.data.pendingToolCalls,
  ].findLast(
    call => !isHiddenTool(call.toolName) && !doneIds.has(call.toolCallId)
  );
};

const getToolActivity = (tc: ToolActivityCall, intl: IntlShape) => {
  const skill = getSkillName(tc);
  return skill
    ? intl.formatMessage(
        { id: 'features.chat.steps.tool.usingSkill' },
        { tool: tc.toolName, skill }
      )
    : intl.formatMessage(
        { id: 'features.chat.steps.tool.using' },
        { tool: tc.toolName }
      );
};

/**
 * One-line status for the agent's current step, derived from the latest
 * streamed part — the label in the progress indicator swaps as SSE events
 * arrive instead of listing every step.
 */
export const getAgentActivity = (
  parts: ChatUIMessage['parts'],
  intl: IntlShape,
  isGeneratingDiagram: boolean
): string => {
  if (isGeneratingDiagram) {
    return intl.formatMessage({ id: 'features.chat.steps.diagramGenerating' });
  }

  const thinking = intl.formatMessage({ id: 'features.chat.thinking.label' });

  const latest = parts.findLast(
    p =>
      p.type === 'reasoning' ||
      p.type === 'text' ||
      (isToolUIPart(p) && toToolCall(p) !== null)
  );

  if (!latest || latest.type === 'reasoning') return thinking;

  if (latest.type === 'text') {
    return latest.state === 'done'
      ? thinking
      : intl.formatMessage({ id: 'features.chat.activity.writing' });
  }

  const tc = isToolUIPart(latest) ? toToolCall(latest) : null;
  if (!tc) return thinking;

  const skill = getSkillName(tc);

  // The skill name only arrives with the complete input, and the skill then
  // loads at once, so the in-flight label never shows it. Name the loaded
  // skill until the next step starts.
  if (tc.state === 'output-available') {
    return tc.toolName === 'skill' && skill
      ? intl.formatMessage(
          { id: 'features.chat.steps.tool.loadedSkill' },
          { skill }
        )
      : thinking;
  }

  if (tc.isError) {
    return intl.formatMessage(
      { id: 'features.chat.steps.tool.failed' },
      { tool: tc.toolName }
    );
  }

  // A subagent's own steps stream under its call; surface the current one.
  const subagentCall = isAgentToolName(tc.toolName)
    ? getSubagentCall(parts, tc.toolCallId)
    : undefined;
  if (subagentCall) {
    return intl.formatMessage(
      { id: 'features.chat.steps.subagent.activity' },
      { agent: tc.toolName, activity: getToolActivity(subagentCall, intl) }
    );
  }

  return getToolActivity(tc, intl);
};
